/**
 * Which element quantity an imported row's Mängd should fill, per element
 * category (DEV-3603).
 *
 * Categories are almost identical in *which* quantities they define, so "does
 * the category define it" tells us nothing useful. What differs is which
 * quantity is the natural one to measure that category by -- a slab by its top
 * area, a column by its length -- so each category gets an explicit order,
 * mirroring the IFC measure priority table.
 *
 * Each list holds at most one quantity per unit. Two entries sharing a unit
 * (`area_top` vs `area_side`, `length` vs `height`) would make a match
 * ambiguous, and there is nothing in a CSV row to break the tie. A list only
 * names quantities its category actually defines.
 */
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  ElementQuantityExpressionName,
  elementQuantityUnitRecord,
} from '@shared/models/element-quantities.model';
import { ElementKind } from '@shared/models/project.model';
import { ConversionFactorUnit } from '@shared/models/unit.model';
import { createElementWithCategory } from './element-category.helpers';
import { getElementQuantityProperties } from './element-quantity-core.helpers';

/** Quantities to try, best first. Categories measured in hr/kWh are absent. */
const MATERIAL_PRIORITY: readonly ElementQuantityExpressionName[] = [
  'mass',
  'volume',
  'area_side',
  'length',
];

export const PRIORITY_BY_CATEGORY: Partial<
  Record<ElementCategoryID, readonly ElementQuantityExpressionName[]>
> = {
  // Two-dimensional by nature: never measured in metres. Wall and Slab define
  // no `mass` row (DEV-3501: geometry-measured, mass is an enrichment output),
  // so mass is absent here rather than filtered later. Each leads with its own
  // `defaultSelectedQuantity`.
  [ElementCategoryID.Slab]: ['area_top', 'volume'],
  [ElementCategoryID.Wall]: ['area_side', 'volume'],
  // Windows & Doors are measured by their opening; mass exists on the category
  // (and is its declared default) but is not what a door is counted in.
  [ElementCategoryID.WindowsDoors]: ['area_side', 'mass', 'volume'],
  // One-dimensional by nature: never measured in square metres, and neither
  // defines a mass row. The metre quantity follows the category's orientation
  // -- a column is vertical, so its extent is height; a beam is horizontal.
  [ElementCategoryID.Column]: ['height', 'volume'],
  [ElementCategoryID.Beam]: ['length', 'volume'],
  // Dimension-agnostic: a material can legitimately be measured any way, and
  // is priced per kg, so mass leads.
  [ElementCategoryID.Concrete]: MATERIAL_PRIORITY,
  [ElementCategoryID.Wood]: MATERIAL_PRIORITY,
  [ElementCategoryID.Insulation]: MATERIAL_PRIORITY,
  [ElementCategoryID.Metal]: MATERIAL_PRIORITY,
  [ElementCategoryID.Gypsum]: MATERIAL_PRIORITY,
  [ElementCategoryID.Ceramics]: MATERIAL_PRIORITY,
  [ElementCategoryID.OtherProduct]: MATERIAL_PRIORITY,
  // `Other` is dimension-agnostic too but defines no mass row.
  [ElementCategoryID.Other]: ['volume', 'area_side', 'length'],
};

/**
 * Element quantities a harmonized CSV unit maps to *unambiguously* (DEV-3387).
 * `kg` is only ever Mass, `m³` only ever Volume — so a row in those units needs
 * no disambiguation. `m²` (side/top/section) and `m` (length/width/height) are
 * deliberately absent: those require the user to pick which quantity.
 */
export const UNAMBIGUOUS_UNIT_QUANTITY: Partial<
  Record<ConversionFactorUnit, ElementQuantityExpressionName>
> = {
  kg: 'mass',
  'm³': 'volume',
};

/**
 * Whether a category's elements define `quantityName` as a quantity row.
 * Structural categories (wall, slab, beam …) deliberately omit `mass` -- they
 * are measured by geometry and mass is an enrichment output -- so a priority
 * entry the category's own model rejects must be skipped (DEV-3501).
 */
export const categoryDefinesQuantity = (
  category_id: ElementCategoryID,
  quantityName: ElementQuantityExpressionName,
): boolean => {
  const element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id,
  });
  return getElementQuantityProperties(element).some(
    (p) => p.name === quantityName,
  );
};

/**
 * The best quantity for `categoryId` that `availableUnits` can actually fill.
 * Entries the category does not define are skipped, so `mass` can sit in every
 * list and simply drop out for the categories that omit it.
 *
 * `undefined` when the category has no list (hr/kWh categories) or the row
 * carries none of its units -- the caller then falls back to the category
 * default and waits for a typed Mängd.
 */
export const resolveImportQuantityByPriority = (
  categoryId: ElementCategoryID | undefined,
  availableUnits: ReadonlySet<ConversionFactorUnit>,
): ElementQuantityExpressionName | undefined => {
  if (categoryId === undefined) return undefined;
  const priority = PRIORITY_BY_CATEGORY[categoryId];
  if (!priority) return undefined;

  return priority.find((name) => {
    const unit = elementQuantityUnitRecord[name] as
      ConversionFactorUnit | undefined;
    return unit !== undefined && availableUnits.has(unit);
  });
};
